import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { FunctionSquare, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { PythonCodeBlock } from "@/components/ui/PythonCodeEditor";
import { useAuthStore } from "@/store/auth";
import {
  useDeletePricingFunction,
  usePricingFunctions,
  useReviewPricingFunction,
} from "../hooks/useTenders";
import { PresetFunctionModal } from "../components/PresetFunctionModal";
import type { PricingPresetFunction } from "../types";
import {
  PRESET_FUNCTION_SORT_LABELS,
  filterPresetFunctions,
  presetFunctionCreators,
  type PresetFunctionSortKey,
} from "../presetFunctionFilters";

const STATUS_BADGE: Record<PricingPresetFunction["status"], { label: string; cls: string }> = {
  pending: { label: "Oczekuje", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  approved: { label: "Dostępna", cls: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  rejected: { label: "Odrzucona", cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
};

export function PresetFunctionsDevPage() {
  const user = useAuthStore((s) => s.user);

  const [statusFilter, setStatusFilter] = useState<"" | PricingPresetFunction["status"]>("");
  const [creatorFilter, setCreatorFilter] = useState("");
  const [sortKey, setSortKey] = useState<PresetFunctionSortKey>("status");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PricingPresetFunction | null>(null);

  const { data: functions = [], isLoading } = usePricingFunctions();
  const reviewMutation = useReviewPricingFunction();
  const deleteMutation = useDeletePricingFunction();

  const creators = useMemo(() => presetFunctionCreators(functions), [functions]);

  const visible = useMemo(
    () => filterPresetFunctions(functions, {
      status: statusFilter, creator: creatorFilter, search, sortKey,
    }),
    [functions, statusFilter, creatorFilter, search, sortKey],
  );

  // Same audience as the API: pricing-catalog users manage functions, developers
  // reach the page to review them even without catalog access.
  if (!user?.has_view_pricing_catalog_permission && !user?.is_developer)
    return <Navigate to="/" replace />;
  // Approving / rejecting / revoking stays with CEO and developers (backend enforces it).
  const canReview = !!user?.is_ceo || !!user?.is_developer;

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (fn: PricingPresetFunction) => {
    setEditing(fn);
    setModalOpen(true);
  };
  const review = (id: string, action: "approve" | "reject" | "revoke") =>
    reviewMutation.mutate({ id, action });

  const selectCls =
    "border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]";

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <FunctionSquare size={20} className="text-[var(--color-primary)]" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Funkcje wycen</h1>
        </div>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {canReview
            ? "Globalne funkcje pozycji presetu — zatwierdzanie, odrzucanie i wycofywanie dostępności."
            : "Globalne funkcje pozycji presetu. Nowa funkcja jest dostępna dopiero po zatwierdzeniu przez CEO lub developera."}
        </p>
      </div>

      {/* Toolbar: filters + sort + search + add */}
      <div className="flex flex-wrap items-center gap-2">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as "" | PricingPresetFunction["status"])} className={selectCls}>
          <option value="">Wszystkie statusy</option>
          <option value="pending">Oczekujące</option>
          <option value="approved">Dostępne</option>
          <option value="rejected">Odrzucone</option>
        </select>
        <select value={creatorFilter} onChange={(e) => setCreatorFilter(e.target.value)} className={selectCls}>
          <option value="">Wszyscy autorzy</option>
          {creators.map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        <select value={sortKey} onChange={(e) => setSortKey(e.target.value as PresetFunctionSortKey)} className={selectCls}>
          {PRESET_FUNCTION_SORT_LABELS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtruj po nazwie…"
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          />
        </div>
        <Button size="sm" onClick={openCreate}>
          <Plus size={13} /> Dodaj funkcję
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : visible.length === 0 ? (
        <div className="text-sm text-gray-400 dark:text-gray-500 py-10 text-center">
          {functions.length === 0 ? "Brak funkcji." : "Brak funkcji pasujących do filtrów."}
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((fn) => {
            const badge = STATUS_BADGE[fn.status];
            return (
              <div key={fn.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{fn.name}</h3>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
                      <span className="text-[10px] text-gray-400 font-mono">
                        {fn.param_names.length} arg.
                      </span>
                    </div>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      Utworzył: {fn.created_by_name ?? "—"}
                      {fn.reviewed_by_name && fn.status !== "pending"
                        ? ` · ${fn.status === "approved" ? "zatwierdził" : "odrzucił"}: ${fn.reviewed_by_name}`
                        : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {canReview && fn.status !== "approved" && (
                      <button
                        onClick={() => review(fn.id, "approve")}
                        disabled={reviewMutation.isPending}
                        className="px-2 py-1 rounded text-[11px] font-medium text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50"
                        title="Oznacz jako dostępną"
                      >
                        Zatwierdź
                      </button>
                    )}
                    {canReview && fn.status === "pending" && (
                      <button
                        onClick={() => review(fn.id, "reject")}
                        disabled={reviewMutation.isPending}
                        className="px-2 py-1 rounded text-[11px] font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                        title="Odrzuć funkcję"
                      >
                        Odrzuć
                      </button>
                    )}
                    {canReview && fn.status === "approved" && (
                      <button
                        onClick={() => review(fn.id, "revoke")}
                        disabled={reviewMutation.isPending}
                        className="px-2 py-1 rounded text-[11px] font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50"
                        title="Wycofaj dostępność (wraca do oczekujących)"
                      >
                        Wycofaj
                      </button>
                    )}
                    <button
                      onClick={() => openEdit(fn)}
                      className="p-1.5 rounded text-gray-400 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-600"
                      title="Edytuj"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Dezaktywować funkcję "${fn.name}"?`)) {
                          deleteMutation.mutate(fn.id);
                        }
                      }}
                      className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-600"
                      title="Usuń"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <PythonCodeBlock code={fn.code} className="mt-2" />
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <PresetFunctionModal open={modalOpen} onClose={() => setModalOpen(false)} editing={editing} />
      )}
    </div>
  );
}
